// AYTM-12710: derive axes / selections / SKU finders for the multi-axis variant selector.
// See specs/questions/ecommerce/product-family.md §Variant Selector.

import { useCallback, useMemo } from 'react'
import { IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../types'
import { useChildrenProducts } from './useChildrenProducts'
import {
  Axis,
  buildAxes,
  findMatchingSKU,
  findSKUForValue,
  isCombinationAvailable,
  isCompatibleWithUpperAxes,
  selectionsOf
} from '../utils/familyAxes'

interface IUseFamilyAxesProps {
  answer: IEcommerceAnswerSchema
  schema: IEcommerceQuestionSchema
}

export interface IFamilyAxes {
  childrenProducts: IEcommerceAnswerSchema[]
  axes: Axis[]
  currentSelections: Record<string, string>
  findMatchingSKU: (target: Record<string, string>) => IEcommerceAnswerSchema | undefined
  findSKUForValue: (axisName: string, value: string) => IEcommerceAnswerSchema | undefined
  isCombinationAvailable: (axisName: string, value: string) => boolean
  isCompatibleWithUpperAxes: (axisIndex: number, value: string) => boolean
}

export const useFamilyAxes = ({ answer, schema }: IUseFamilyAxesProps): IFamilyAxes => {
  const childrenProducts = useChildrenProducts({ answer, schema })

  const axes = useMemo(() => buildAxes(childrenProducts), [childrenProducts])

  const currentSelections = useMemo(() => selectionsOf(answer), [answer])

  const findMatchingSKUCb = useCallback(
    (target: Record<string, string>) => findMatchingSKU(childrenProducts, target),
    [childrenProducts]
  )
  const findSKUForValueCb = useCallback(
    (axisName: string, value: string) => findSKUForValue(childrenProducts, currentSelections, axisName, value),
    [childrenProducts, currentSelections]
  )
  const isCombinationAvailableCb = useCallback(
    (axisName: string, value: string) => isCombinationAvailable(childrenProducts, currentSelections, axisName, value),
    [childrenProducts, currentSelections]
  )
  const isCompatibleWithUpperAxesCb = useCallback(
    (axisIndex: number, value: string) =>
      isCompatibleWithUpperAxes(childrenProducts, axes, currentSelections, axisIndex, value),
    [childrenProducts, axes, currentSelections]
  )

  return {
    childrenProducts,
    axes,
    currentSelections,
    findMatchingSKU: findMatchingSKUCb,
    findSKUForValue: findSKUForValueCb,
    isCombinationAvailable: isCombinationAvailableCb,
    isCompatibleWithUpperAxes: isCompatibleWithUpperAxesCb
  }
}
